import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { concat, pick, includes, chain, isEmpty } from "lodash"
import { post as postApi, put as putApi } from "api"
import { call, put, select, takeEvery } from "redux-saga/effects"
import { Creators as modelCreators } from "model/data"
import { Creators as creatorCreators } from "model/creator"
import { Creators as notificationCreators } from "modules/notifications/model"
import { handleUpdateRecentInstruments } from "../../../sagas/app/recentInstruments"
import { getSelectedDate } from "selectors/dates"
import { squashUnmodeledBaskets } from "helpers/swaps"

function getCollectionToReset(resourceType) {
  if (resourceType === "RESEARCH_TOPIC") {
    return ["topicExperiments"]
  }

  return ["experimentMeta", "experiment", "portfolioExperiments", "experimentDate"]
}

function* createExperiment(account, resourceType, resourceId, experiment, searchType) {
  const resourceTypePath = resourceType === "RESEARCH_TOPIC" ? "t" : "p"
  const experimentResult = yield call(
    postApi,
    `/api/${account}/${resourceTypePath}/${resourceId}/experiments`,
    pick(experiment, ["name", "type", "modelId", "config", "description"]),
  )
  yield call(handleUpdateRecentInstruments, account, experimentResult.id, "experiment", searchType, { skipModelId: true })
  return experimentResult
}

function* uploadExperimentDate(experimentId, experiment, account, date) {
  const rawPositionSet = experiment.positionSet
  if (!rawPositionSet) {
    return { uploadCompleted: false }
  }

  // Normalizing dates to an array so the position set can be "squashed"
  const positionSetForSquash = rawPositionSet.dates ? rawPositionSet : { dates: [rawPositionSet] }
  const squashed = squashUnmodeledBaskets(positionSetForSquash)
  const flatDate = squashed.dates[0]
  if (!flatDate) {
    return { uploadCompleted: false }
  }

  const data = {
    ...flatDate,
    date: flatDate.date ?? date,
    equity: flatDate.equity ?? experiment.equity,
  }

  return yield call(
    postApi,
    `/api/${account}/e/${experimentId}/positionSet`,
    { data },
  )
}

function* updateExperiment(account, updatedExperiment) {
  return yield call(
    putApi,
    `/api/${account}/e/${updatedExperiment.id}`,
    chain(updatedExperiment)
      .pick(["name", "description", "modelId", "config"])
      .omitBy(isEmpty)
      .value(),
  )
}

function* dispatchCreateExperimentSuccessAction(account, resourceId, resourceType, createdExperiment) {
  if (resourceType === "RESEARCH_TOPIC") {
    yield put(creatorCreators.createSuccess("topicExperiments", `/api/${account}/t/${resourceId}/experiments`, createdExperiment))
  } else {
    yield put(modelCreators.graphqlCreateSuccess("portfolioExperiments", `portfolioExperiments-${resourceId}`, "portfolio.experiments", createdExperiment))
  }
}

export const { Types, Creators } = actionCreator("ompnt/modules/save-experiment/", {
  reset: [],
  saveAndCompare: {
    props: ["experiment", "resourceType", "resourceId", "searchType"],
    track: true,
    trackType: "save-experiment",
    trackPayload: ({ experiment }) => pick(experiment, ["name", "type", "description"]),
  },
  saveAndCompareSuccess: ["newExperiment"],
  saveOptimizeAndCompare: ["experiment", "searchType"],
  saveSmartTradeAndCompare: {
    props: ["experiment", "researchTopicId", "searchType"],
    track: true,
    trackType: "smart-trade-save-experiment",
  },
  update: {
    props: ["updatedExperiment"],
    track: true,
    trackType: "update-experiment",
    trackPayload: ({ updatedExperiment }) => pick(updatedExperiment, ["name", "type", "description"]),
  },
  updateMetaAndUploadSuccess: ["updatedExperimentId"],
  updateMetaSuccess: ["updatedExperimentId"],
  resetDataCollections: ["index", "updatedExperiment"],
  rename: ["updatedExperiment"],
})

const DEFAULT_STATE = {
  newExperiment: null,
  updatedExperimentId: "",
}

function handleSaveAndCompare(state) {
  return { ...state, newExperiment: null }
}

function handleSaveAndCompareSuccess(state, { newExperiment }) {
  return { ...state, newExperiment }
}

function handleUpdateMetaAndUploadSuccess(state, { updatedExperimentId }) {
  return { ...state, updatedExperimentId }
}

function handleUpdateMetaSuccess(state, { updatedExperimentId }) {
  return { ...state, updatedExperimentId }
}

function handleReset(state) {
  return { ...state, ...DEFAULT_STATE }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SAVE_AND_COMPARE]: handleSaveAndCompare,
  [Types.SAVE_OPTIMIZE_AND_COMPARE]: handleSaveAndCompare,
  [Types.SAVE_SMART_TRADE_AND_COMPARE]: handleSaveAndCompare,
  [Types.SAVE_AND_COMPARE_SUCCESS]: handleSaveAndCompareSuccess,
  [Types.UPDATE_META_AND_UPLOAD_SUCCESS]: handleUpdateMetaAndUploadSuccess,
  [Types.UPDATE_META_SUCCESS]: handleUpdateMetaSuccess,
  [Types.RESET]: handleReset,
})

function* saveAndCompare({ experiment, resourceType, resourceId, searchType }) {
  const { account, date } = yield select(state => ({
    account: state.global.selected.account,
    date: getSelectedDate(state),
  }))

  try {
    const createResult = yield call(createExperiment, account, resourceType, resourceId, experiment, searchType)

    if (createResult && createResult.id) {
      const { id: experimentId, name: experimentName } = createResult
      const createdExperiment = {
        ...pick(createResult, ["id", "name", "type", "description"]),
        availableFrom: date,
      }

      if (experiment.positionSet) {
        const uploadDateResult = yield call(uploadExperimentDate, experimentId, experiment, account, date)

        if (uploadDateResult.uploadCompleted) {
          yield put(Creators.saveAndCompareSuccess({
            type: "experiment",
            name: experimentName,
            id: experimentId,
            date: experiment.type === "OPTIMIZATION" ? date : null,
          }))
        } else {
          createdExperiment.availableFrom = null
          yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading data to the experiment."))
        }
      } else {
        createdExperiment.availableFrom = null
        yield put(Creators.saveAndCompareSuccess({ type: "experiment", name: experimentName, id: experimentId }))
      }

      yield dispatchCreateExperimentSuccessAction(account, resourceId, resourceType, createdExperiment)
    } else {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while creating the experiment."))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while creating the experiment."))
  }
}

function* saveOptimizeAndCompare({ experiment, searchType }) {
  const { account, portfolioId, date } = yield select(state => ({
    portfolioId: state.global.selected.portfolio,
    account: state.global.selected.account,
    date: getSelectedDate(state),
  }))

  try {
    const result = yield call(createExperiment, account, "PORTFOLIO", portfolioId, experiment, searchType)

    if (!result || !result.id) {
      yield put(notificationCreators.add("error", null, "An error happened while creating the experiment."))
    } else {
      const newExperiment = pick(result, ["id", "name", "description", "modelId", "type"])
      const uploadResult = yield call(uploadExperimentDate, newExperiment.id, experiment, account, date)
      const createdExperiment = { ...newExperiment, availableFrom: date }

      if (!uploadResult.uploadCompleted) {
        createdExperiment.availableFrom = null
        yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading data to the experiment."))
      } else {
        yield put(Creators.saveAndCompareSuccess({
          type: "experiment",
          name: newExperiment.name,
          id: newExperiment.id,
          modelId: newExperiment.modelId,
          date,
        }))
      }

      yield dispatchCreateExperimentSuccessAction(account, portfolioId, "PORTFOLIO", createdExperiment)
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while creating the experiment."))
  }
}

function* saveSmartTradeAndCompare({ experiment, researchTopicId, searchType }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  try {
    const result = yield call(createExperiment, account, "RESEARCH_TOPIC", researchTopicId, experiment, searchType)

    if (!result || !result.id) {
      yield put(notificationCreators.add("error", null, "An error happened while creating the smart trade."))
    } else {
      const newExperiment = pick(result, ["id", "name", "description", "type", "modelId", "config"])
      const positionSet = experiment.positionSet.dates[0]
      const createdExperiment = { ...newExperiment, availableFrom: positionSet.date }

      const uploadResult = yield call(uploadExperimentDate, newExperiment.id, { positionSet }, account, positionSet.date)
      if (!uploadResult.uploadCompleted) {
        createdExperiment.availableFrom = null
        yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading data to the smart trade."))
      } else {
        yield put(Creators.saveAndCompareSuccess({ type: "experiment", name: newExperiment.name, id: newExperiment.id }))
      }

      yield dispatchCreateExperimentSuccessAction(account, researchTopicId, "RESEARCH_TOPIC", createdExperiment)
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while creating the smart trade."))
  }
}

function* update({ updatedExperiment }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  try {
    let hasError = false

    if (includes(["SIMULATION", "OPTIMIZATION", "SMART_TRADE"], updatedExperiment.type)) {
      const result = yield call(updateExperiment, account, updatedExperiment)
      if (!result || !result.id) {
        hasError = true
        yield put(notificationCreators.add("error", null, "An error happened while updating the experiment."))
      }
    }

    if (!hasError && updatedExperiment.positionSet) {
      const uploadResult = yield call(uploadExperimentDate, updatedExperiment.id, updatedExperiment, account, updatedExperiment.positionSet.date)

      if (!uploadResult || !uploadResult.uploadCompleted) {
        yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading data to the experiment."))
      } else {
        yield put(Creators.updateMetaAndUploadSuccess(updatedExperiment.id))
      }
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while updating the experiment."))
  }
}

function* rename({ updatedExperiment }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  const experimentData = pick(updatedExperiment, ["id", "name", "description"])

  try {
    const result = yield call(updateExperiment, account, experimentData)

    if (!result || !result.id) {
      yield put(notificationCreators.add("error", null, "An error happened while updating the experiment."))
    } else {
      yield put(modelCreators.resetCollections(getCollectionToReset(updatedExperiment.resourceType)))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while updating the experiment."))
  }
}

function* resetExperiments() {
  yield put(modelCreators.resetCollections(["experiments"]))
}

function* resetDataCollections(action) {
  const { index, updatedExperiment } = action
  let collections = [...getCollectionToReset(updatedExperiment.resourceType)]
  if (index) {
    collections = concat(collections, [
      `compareFrontierPositionSet${index}`,
      `comparePerformancePositionSet${index}`,
      `comparePerformanceMetrics${index}`,
      `compareRiskPositionSet${index}`,
      `compareExposurePositionSet${index}`,
      `compareCompositionPositionSet${index}`,
      `compareCompositionPositionData${index}`,
    ])
  }

  yield put(modelCreators.resetCollections(collections))
}

export function* saga() {
  yield takeEvery(Types.SAVE_AND_COMPARE, saveAndCompare)
  yield takeEvery(Types.SAVE_OPTIMIZE_AND_COMPARE, saveOptimizeAndCompare)
  yield takeEvery(Types.SAVE_SMART_TRADE_AND_COMPARE, saveSmartTradeAndCompare)
  yield takeEvery(Types.SAVE_AND_COMPARE_SUCCESS, resetExperiments)
  yield takeEvery(Types.UPDATE, update)
  yield takeEvery(Types.RENAME, rename)
  yield takeEvery(Types.RESET_DATA_COLLECTIONS, resetDataCollections)
}
